import type { Component } from "solid-js";
import { useScene, useTyping } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import type { AssetSource } from "~/lib/integrations/integrationProviders";
import { AppSidebar, LibraryPage, MemoriesPanel, T } from "./internal/library";
import { MockScene, type Person } from "./internal/parts";
import { ScaledFrame } from "./ScaledFrame";

export type LibraryLabel = { name: string; color: string; icon: Component<{ class?: string }> };

/**
 * A file as the Library lists it. `name` is what the row shows; `filename`
 * only when it differs, as it does for a synced page or issue.
 */
export type LibraryFile = {
  name: string;
  filename?: string;
  source?: AssetSource;
  labels: LibraryLabel[];
  /** Local time the file last changed. */
  at: string;
};

/** `date` is the ISO day the memory was saved. */
export type LibraryMemory = { text: string; date: string };

export type LibraryData = {
  url: string;
  project: string;
  user: Person;
  /** The sidebar's recent tasks, all from today. */
  tasks: { title: string; at: string }[];
  query: string;
  /** Newest first, before the search. */
  files: LibraryFile[];
  /** The file that lands mid-search; its name matches the query. */
  landed: LibraryFile;
  /** The project's memory count before `saved` arrives. */
  memoryCount: number;
  memories: LibraryMemory[];
  /** The memory saved and then pinned during the scene. */
  saved: LibraryMemory;
};

/**
 * The web app with a project's Library open and its memories in the side
 * panel, played once: a file-name search narrows the list, a new file lands
 * on top and finishes processing, and a new project memory arrives and is
 * pinned.
 */
export const LibraryMockup: Component<{ data: LibraryData; class?: string }> = (props) => {
  const { t } = useI18n();
  const scene = useScene();
  const typed = useTyping(() => props.data.query, scene, T.typing * 1000);

  return (
    <MockScene scene={scene} label={t("marketing.mock.library.scene")} class={props.class}>
      <ScaledFrame
        chrome="browser"
        width={1280}
        height={720}
        narrow={{ width: 400, height: 980 }}
        title={props.data.url}
      >
        <div inert class="flex h-full flex-col bg-canvas @3xl:flex-row">
          <div class="hidden @3xl:flex">
            <AppSidebar data={props.data} />
          </div>
          <div class="min-h-0 min-w-0 flex-1 overflow-hidden">
            <LibraryPage data={props.data} typed={typed()} />
          </div>
          <div class="h-110 shrink-0 p-2 pt-0 @3xl:m-0.5 @3xl:h-[calc(100%-0.25rem)] @3xl:w-120 @3xl:p-0">
            <MemoriesPanel data={props.data} />
          </div>
        </div>
      </ScaledFrame>
    </MockScene>
  );
};
